import React from "react";
import { PropTypes } from "prop-types";
// Material imports
import withStyles from "@mui/styles/withStyles";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
// Limitless imports
import TextField from "@limitless/modern/inputs/text-field/TextField";
import { withi18n } from "../../../../customer/components";
import { PortalTextInput } from "../../..";
import FilePickerField from "../../file-picker-field/FilePickerField";
import { validateImageAttachment } from "../../../../expert";
import Dialog from "@limitless/modern/feedback/dialog/Dialog";
import RadioGroup from "@limitless/modern/inputs/common-radio-group/RadioGroup";
const IMAGE_FILE_TYPE = ["image/bmp", "image/jpeg", "image/png"];
const MessageAttachmentDialog = ({
  classes,
  t,
  message,
  onCancelClick,
  onAttachClick,
  includeFileUrl = false,
  onUrlAttachmentConfirmClick,
  dialogueTypes,
  attachmentDialogueType,
  onOptionChange
}) => {
  const [caption, setCaption] = React.useState("");
  const [file, setFile] = React.useState(null);
  const [imageBase64Url, setImageBase64Url] = React.useState("");
  const [url, setUrl] = React.useState("");
  const [imageError, setImageError] = React.useState("");
  const [hasMaliciousContent, setHasMaliciousContent] = React.useState(false);
  const handleFileChange = file => {
    const {
      imageAttachmentError
    } = validateImageAttachment(file);
    setImageError(imageAttachmentError);
    setFile(file);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      const base64Url = reader.result;
      setImageBase64Url(base64Url);
    };
  };
  const handleCancelFile = () => {
    setFile(null);
    setImageBase64Url("");
  };
  const handleConfirmClick = () => {
    if (url === "") {
      onAttachClick(file, caption);
    } else {
      onUrlAttachmentConfirmClick(url, caption);
    }
  };
  const isConfirmDisabled = React.useMemo(() => {
    if (file && imageError === "") {
      return false;
    }
    if (includeFileUrl && url === "") {
      return true;
    }
    if (file || includeFileUrl) {
      return false;
    }
    return true;
  }, [includeFileUrl, url, file, imageError]);
  return /*#__PURE__*/React.createElement(Dialog, {
    open: true,
    onClose: () => onCancelClick(false),
    "aria-labelledby": "confirmExpertAttachment",
    id: "confirmExpertAttachment",
    title: "Attach an image",
    onCancelClick: () => onCancelClick(false),
    onOkClick: () => handleConfirmClick(),
    okDisabled: hasMaliciousContent || isConfirmDisabled,
    okText: "Attach",
    cancelVariant: "text",
    paperStyle: {
      maxWidth: "470px"
    },
    titleStyle: {
      paddingBottom: 1
    }
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 3,
    direction: "column"
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption"
  }, t("Attachments should be relevant to the conversation and not contain any sensitive or personal information."))), message.conversationState.showChooseAttachmentOption && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    className: classes.subContainer
  }, /*#__PURE__*/React.createElement(RadioGroup, {
    id: "radio-group-dialogue-type",
    label: "Dialogue Type",
    value: dialogueTypes.findIndex(option => option.value === attachmentDialogueType),
    options: dialogueTypes,
    onClick: index => {
      onOptionChange(dialogueTypes[index].value);
    }
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    className: classes.subContainer
  }, imageError === "" && file ? /*#__PURE__*/React.createElement(Grid, {
    container: true,
    justifyContent: "center"
  }, /*#__PURE__*/React.createElement(Grid, {
    className: classes.imagePreviewContainer
  }, /*#__PURE__*/React.createElement("img", {
    src: imageBase64Url,
    className: classes.imagePreview,
    alt: "preview"
  })), /*#__PURE__*/React.createElement(IconButton, {
    className: classes.CloseButton,
    onClick: handleCancelFile
  }, /*#__PURE__*/React.createElement(CloseIcon, {
    className: classes.icon
  })), /*#__PURE__*/React.createElement(Grid, {
    className: classes.imagePreviewText
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption"
  }, t(`Name of file: ${file.name}`)))) : /*#__PURE__*/React.createElement(FilePickerField, {
    id: "browseFile",
    acceptCriteria: IMAGE_FILE_TYPE.join("|"),
    loading: false,
    onChange: handleFileChange,
    value: file,
    error: imageError,
    previewImage: true
  })), includeFileUrl && !file && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    className: classes.subContainer
  }, /*#__PURE__*/React.createElement(PortalTextInput, {
    id: "attachmentUrl",
    label: "URL",
    onChange: event => setUrl(event.target.value),
    value: url,
    fullWidth: true,
    showHelperText: false
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    className: `${classes.subContainer}${classes.descContainer}`
  }, /*#__PURE__*/React.createElement(PortalTextInput, {
    id: "expertAttachmentCaption",
    label: "Description",
    onChange: event => setCaption(event.target.value),
    value: caption,
    fullWidth: true,
    variant: "caption",
    helperText: false,
    showHelperText: true,
    containerClass: `${classes.textInput}`,
    onMaliciousDetected: setHasMaliciousContent,
    color: "primary",
    placeholder: "Type here",
    InputProps: {
      sx: {
        "& .MuiInputBase-input": {
          padding: 0
        }
      }
    }
  }))));
};
const styles = theme => ({
  dialogContainer: {
    backgroundColor: theme.background.paper,
    overflowY: "hidden"
  },
  dialogTitle: {
    paddingBottom: "4px"
  },
  subContainer: {
    paddingTop: "16px!important"
  },
  textInput: {
    display: "flex",
    flexDirection: "column",
    marginBottom: 16,
    marginTop: 16,
    padding: "0px"
  },
  imagePreviewContainer: {
    height: 189,
    width: 240
  },
  imagePreview: {
    height: "100%",
    width: "100%",
    marginBottom: 11,
    borderRadius: 4,
    boxShadow: "0px 3px 8px -4px #00000060"
  },
  noPadding: {
    padding: 0
  },
  CloseButton: {
    marginLeft: -15,
    padding: 0,
    height: 25,
    width: 25,
    marginTop: -10,
    backgroundColor: theme.background.paper,
    borderRadius: "50%",
    boxShadow: "0px 3px 8px -4px #00000060",
    zIndex: 999,
    "&:hover": {
      backgroundColor: theme.background.paper
    }
  },
  imagePreviewText: {
    width: 250,
    marginTop: 5
  },
  descContainer: {
    marginTop: 16
  },
  icon: {
    fontSize: 16
  }
});
export default withStyles(styles)(withi18n(MessageAttachmentDialog));
MessageAttachmentDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  message: PropTypes.object.isRequired,
  onCancelClick: PropTypes.func.isRequired,
  onAttachClick: PropTypes.func.isRequired,
  includeFileUrl: PropTypes.bool,
  onUrlAttachmentConfirmClick: PropTypes.func,
  dialogueTypes: PropTypes.object,
  attachmentDialogueType: PropTypes.string,
  onOptionChange: PropTypes.func
};